import { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useText } from '../../../hooks/useText';
import { formatNumber } from '../helpers';
import { trackEvent } from '@/utils/analytics';
import {
  GDAS_STATS_COLUMNS,
  VDAS_STATS_COLUMNS,
  loadLatestSourceStats,
} from '../../../services/sourceStatsService';

type StatsTab = 'gda' | 'vda';
type SortDirection = 'asc' | 'desc';

type SourceStatsRow = {
  source: string;
  [key: string]: string | number;
};

function cellValue(row: SourceStatsRow, key: string): string | number | undefined {
  return row[key];
}

const PINK = 'var(--pink-color)';
const NAVY = 'var(--blue-color)';

function compact(n: number | string | undefined): string {
  if (n === undefined || n === null) return '—';
  const num = typeof n === 'number' ? n : Number(n);
  if (Number.isNaN(num)) return '—';
  if (num >= 1_000_000) {
    const v = num / 1_000_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (num >= 1_000) {
    const v = num / 1_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
  }
  return num.toLocaleString();
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span
      className="inline-flex max-w-full items-center rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs font-semibold tracking-wide text-slate-600"
      title={source}
    >
      <span className="truncate">{source}</span>
    </span>
  );
}

function SummaryStrip({
  accentColor,
  items,
}: {
  accentColor: string;
  items: Array<{ label: string; value: number | string | undefined }>;
}) {
  return (
    <div
      className="rounded-xl border border-gray-100 bg-white px-4 py-4 flex flex-wrap justify-between gap-4"
      style={{ borderTop: `3px solid ${accentColor}` }}
    >
      {items.map(item => (
        <div key={item.label} className="min-w-0">
          <p className="text-xl font-bold tabular-nums text-gray-800" title={formatNumber(item.value)}>
            {compact(item.value)}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  if (!active) {
    return <span className="inline-block w-3.5 h-3.5 opacity-30" aria-hidden />;
  }
  return direction === 'asc' ? (
    <ChevronUpIcon className="w-3.5 h-3.5 inline-block" aria-hidden />
  ) : (
    <ChevronDownIcon className="w-3.5 h-3.5 inline-block" aria-hidden />
  );
}

function SourceStatsTable({
  rows,
  columns,
  labels,
  shortLabels,
  accentColor,
  defaultSortKey,
}: {
  rows: SourceStatsRow[];
  columns: readonly string[];
  labels: Record<string, string>;
  shortLabels?: Record<string, string>;
  accentColor: string;
  defaultSortKey: string;
}) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const { bodyRows, totalRow } = useMemo(() => {
    const total = rows.find(r => String(r.source).toUpperCase() === 'ALL') ?? null;
    const rest = rows.filter(r => String(r.source).toUpperCase() !== 'ALL');

    const sorted = [...rest].sort((a, b) => {
      const aVal = cellValue(a, sortKey);
      const bVal = cellValue(b, sortKey);

      if (sortKey === 'source') {
        const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''));
        return sortDirection === 'asc' ? cmp : -cmp;
      }

      const aNum = Number(aVal ?? 0);
      const bNum = Number(bVal ?? 0);
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });

    return { bodyRows: sorted, totalRow: total };
  }, [rows, sortKey, sortDirection]);

  const onSort = (column: string) => {
    if (sortKey === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(column);
    setSortDirection(column === 'source' ? 'asc' : 'desc');
  };

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-5 py-10 text-center text-sm text-gray-400">
        No statistics available for this release.
      </div>
    );
  }

  const numericColCount = columns.length - 1;
  const sourceWidth = '22%';
  const numericWidth = `${(78 / numericColCount).toFixed(2)}%`;

  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden w-full">
      <table className="w-full table-fixed text-xs sm:text-sm">
        <colgroup>
          {columns.map(column => (
            <col key={column} style={{ width: column === 'source' ? sourceWidth : numericWidth }} />
          ))}
        </colgroup>
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80">
            {columns.map(column => (
              <th
                key={column}
                scope="col"
                className={`px-1.5 sm:px-2 py-2.5 font-semibold text-gray-500 ${
                  column === 'source' ? 'text-left' : 'text-right'
                }`}
              >
                <button
                  type="button"
                  className={`inline-flex max-w-full items-center gap-0.5 hover:text-gray-800 transition-colors ${
                    column === 'source' ? '' : 'ml-auto'
                  }`}
                  onClick={() => onSort(column)}
                  title={labels[column] ?? column}
                  aria-label={`Sort by ${labels[column] ?? column}`}
                  aria-sort={
                    sortKey === column ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                >
                  <SortIcon active={sortKey === column} direction={sortDirection} />
                  <span className="truncate leading-tight">
                    {shortLabels?.[column] ?? labels[column] ?? column}
                  </span>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map(row => {
            const source = String(row.source ?? '');
            return (
              <tr key={source} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40">
                {columns.map(column => (
                  <td
                    key={column}
                    className={`px-1.5 sm:px-2 py-2 ${
                      column === 'source' ? 'text-left' : 'text-right tabular-nums text-gray-700'
                    }`}
                  >
                    {column === 'source' ? (
                      <SourceBadge source={source} />
                    ) : (
                      formatNumber(cellValue(row, column))
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {totalRow && (
          <tfoot>
            <tr className="border-t-2 bg-white" style={{ borderTopColor: accentColor }}>
              {columns.map(column => (
                <td
                  key={column}
                  className={`px-1.5 sm:px-2 py-2.5 font-bold ${
                    column === 'source' ? 'text-left' : 'text-right tabular-nums text-gray-800'
                  }`}
                >
                  {column === 'source' ? (
                    <SourceBadge source={String(totalRow.source)} />
                  ) : (
                    formatNumber(cellValue(totalRow, column))
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-24 rounded-xl bg-gray-100" />
      <div className="h-64 rounded-xl bg-gray-100" />
    </div>
  );
}

export default function AboutStatsBySource() {
  const titleGDAs = useText('about.statistics.gdas_title');
  const titleVDAs = useText('about.statistics.vdas_title');

  const labelSource = useText('about.statistics.label_source');
  const labelGenes = useText('about.statistics.label_genes');
  const labelDiseases = useText('about.statistics.label_diseases');
  const labelGDAs = useText('about.statistics.label_gdas');
  const labelVariants = useText('about.statistics.label_variants');
  const labelVDAs = useText('about.statistics.label_vdas');
  const labelEvidences = useText('about.statistics.label_evidences');
  const labelPublications = useText('about.statistics.label_publications');
  const labelChemicals = useText('about.statistics.label_chemicals');

  const [activeTab, setActiveTab] = useState<StatsTab>('gda');
  const [loading, setLoading] = useState(true);
  const [gdasRows, setGdasRows] = useState<SourceStatsRow[]>([]);
  const [vdasRows, setVdasRows] = useState<SourceStatsRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    loadLatestSourceStats()
      .then(stats => {
        if (cancelled) return;
        setGdasRows(stats.gdasRows);
        setVdasRows(stats.vdasRows);
      })
      .catch(e => {
        console.error('Error loading About stats by source:', e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const gdaAll = useMemo(() => gdasRows.find(r => String(r.source).toUpperCase() === 'ALL'), [gdasRows]);
  const vdaAll = useMemo(() => vdasRows.find(r => String(r.source).toUpperCase() === 'ALL'), [vdasRows]);

  const gdaLabels: Record<string, string> = {
    source: labelSource,
    genes: labelGenes,
    diseases: labelDiseases,
    gdas: labelGDAs,
    Gassocs: labelEvidences,
    Gpapers: labelPublications,
    Gchemicals: labelChemicals,
  };

  const gdaShortLabels: Record<string, string> = {
    source: labelSource,
    genes: labelGenes,
    diseases: labelDiseases,
    gdas: labelGDAs,
    Gassocs: 'Evid.',
    Gpapers: 'Pubs',
    Gchemicals: 'Chem.',
  };

  const vdaLabels: Record<string, string> = {
    source: labelSource,
    variants: labelVariants,
    Vdiseases: labelDiseases,
    vdas: labelVDAs,
    Vassocs: labelEvidences,
    Vpapers: labelPublications,
    Vchemicals: labelChemicals,
  };

  const vdaShortLabels: Record<string, string> = {
    source: labelSource,
    variants: labelVariants,
    Vdiseases: labelDiseases,
    vdas: labelVDAs,
    Vassocs: 'Evid.',
    Vpapers: 'Pubs',
    Vchemicals: 'Chem.',
  };

  const onTabChange = (tab: StatsTab) => {
    setActiveTab(tab);
    trackEvent({
      name: 'tab_click',
      buttonId: `docs_stats_by_source_${tab}`,
      label: tab === 'gda' ? 'GDA' : 'VDA',
      page: 'docs',
      section: 'stats_by_source',
      feature: 'stats_by_source',
      eventCategory: 'docs',
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <h5 className="mb-1 text-lg font-semibold text-gray-800">Stats by Source</h5>
        <p className="text-sm text-gray-400">
          Breakdown of associations, evidences and entities per data source in the latest release.
        </p>
      </div>

      <div
        className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
        role="tablist"
        aria-label="Stats by source type"
      >
        {(
          [
            { id: 'gda' as const, label: titleGDAs || 'GDAs', color: PINK },
            { id: 'vda' as const, label: titleVDAs || 'VDAs', color: NAVY },
          ] as const
        ).map(tab => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                selected ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              style={selected ? { color: tab.color } : undefined}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <LoadingState />
      ) : activeTab === 'gda' ? (
        <div className="space-y-4" role="tabpanel">
          <SummaryStrip
            accentColor={PINK}
            items={[
              { label: labelGDAs, value: gdaAll?.gdas },
              { label: labelGenes, value: gdaAll?.genes },
              { label: labelDiseases, value: gdaAll?.diseases },
              { label: labelEvidences, value: gdaAll?.Gassocs },
              { label: labelPublications, value: gdaAll?.Gpapers },
              { label: labelChemicals, value: gdaAll?.Gchemicals },
            ]}
          />
          <SourceStatsTable
            rows={gdasRows}
            columns={GDAS_STATS_COLUMNS}
            labels={gdaLabels}
            shortLabels={gdaShortLabels}
            accentColor={PINK}
            defaultSortKey="gdas"
          />
        </div>
      ) : (
        <div className="space-y-4" role="tabpanel">
          <SummaryStrip
            accentColor={NAVY}
            items={[
              { label: labelVDAs, value: vdaAll?.vdas },
              { label: labelVariants, value: vdaAll?.variants },
              { label: labelDiseases, value: vdaAll?.Vdiseases },
              { label: labelEvidences, value: vdaAll?.Vassocs },
              { label: labelPublications, value: vdaAll?.Vpapers },
              { label: labelChemicals, value: vdaAll?.Vchemicals },
            ]}
          />
          <SourceStatsTable
            rows={vdasRows}
            columns={VDAS_STATS_COLUMNS}
            labels={vdaLabels}
            shortLabels={vdaShortLabels}
            accentColor={NAVY}
            defaultSortKey="vdas"
          />
        </div>
      )}
    </div>
  );
}
